import { JSX, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { LuDoorOpen, LuKeyRound } from 'react-icons/lu'
import { useDispatch } from 'react-redux'
import { Text } from '@chakra-ui/react'
import { SublocationWithDevices } from '@leid/api-locations'
import { Order, OrderItem, PickupStatus } from '@leid/api-orders'
import Router from 'next/router'

import { OrderItemInfo } from 'appTypes/orders'
import { LeidButton, LeidButtonWhiteOutlined, LeidYellowButton } from 'components/ui/Buttons/Button'
import { formatItemCardDateDate } from 'components/ui/ItemCard/helpers'
import { Loader } from 'components/ui/Loader'
import apiClient from 'helpers/clients/apiClient'
import { useCustomerInfo } from 'hooks/useCustomerInfo'
import { displaySnackbar } from 'store/partials/snackbar/snackbarSlice'
import { getDifferenceInHours, parseUtcToMoment, utcNow } from 'utils/dates'
import { getProlongationPageUrl } from 'utils/redirectUrlsBuilders'

import { getDoorsWithDirection } from '../utils/devices'
import { getOrderItemPickupStatus } from '../utils/orderItems'

import AssetsManagementFlowModal from './pickupReturnFlow/AssetsManagementFlowModal'
import { DeviceDirection, FlowDirection } from './pickupReturnFlow/models'
import InfoTag from './InfoTag'
import { OrderItemCircleStatus, OrderItemPickupStatus } from './models'
import OpenDoorsModal from './OpenDoorsModal'
import PinCode from './PinCode'
import OrderItemStatusCircle from './StatusCircle'

type Props = {
  orderIsLoading: boolean
  orderItemInfo: OrderItemInfo
  orderItems: OrderItem[]
  order: Order
  refetchOrder: () => void
  isPublicPage: boolean
}

const OrderItemPickupControl = ({
  orderItemInfo,
  orderItems,
  refetchOrder,
  orderIsLoading,
  order,
  isPublicPage,
}: Props): JSX.Element => {
  const { orderItem } = orderItemInfo

  const { t } = useTranslation('common')
  const dispatch = useDispatch()

  const [isLoading, setIsLoading] = useState(true)
  const [assetsManagementModalDirection, setAssetsManagementModalDirection] = useState<FlowDirection>(
    FlowDirection.NONE,
  )
  const [openOpenDoorsModal, setOpenOpenDoorsModal] = useState(false)
  const [hasOutDoors, setHasOutDoors] = useState(false)
  const [devices, setDevices] = useState<SublocationWithDevices[]>([])

  const pickupStatus = getOrderItemPickupStatus(orderItemInfo, order)

  const { customerInfo } = useCustomerInfo()

  useEffect(() => {
    initializeGatesInfo()
  }, [orderItem])

  const initializeGatesInfo = async () => {
    setIsLoading(true)

    const isActive = orderItemIsActive()
    if (!isActive || !orderItem.locationId) {
      setIsLoading(false)
      setHasOutDoors(false)
      return
    }

    if (orderItem.locationId) {
      const response = await apiClient.getSublocationDevices({
        locationIds: [orderItem.locationId],
      })
      if (!response.success || !response.data) {
        setIsLoading(false)
        setHasOutDoors(false)
        dispatch(displaySnackbar({ message: t('error'), isError: true }))
        return
      }

      const devices = response.data
      setDevices(devices)

      const outDoors = getDoorsWithDirection(DeviceDirection.OUT, devices)
      setHasOutDoors(outDoors.length > 0)
    }

    setIsLoading(false)
  }

  const orderItemIsActive = () => {
    const { rentedFrom, rentedTo } = orderItem

    if (!rentedFrom || !rentedTo) {
      return false
    }

    const now = utcNow()
    const fromDate = parseUtcToMoment(rentedFrom)?.subtract(15, 'minutes').toDate() ?? now

    if (
      (orderItem.pickupStatus === PickupStatus.EMPTY ||
        orderItem.pickupStatus === PickupStatus.PICKEDUP ||
        orderItem.pickupStatus === PickupStatus.RETURNED) &&
      order.paidDate
    ) {
      const difference = getDifferenceInHours(order.paidDate, rentedFrom)
      if (difference > 0 && difference <= 1) {
        // If order was paid less than an hour before order item start - make it available for pickup
        return true
      }
    }

    if (rentedTo > now && fromDate < now) {
      return true
    }

    return false
  }

  const openPickupOrderItemModal = () => {
    setAssetsManagementModalDirection(FlowDirection.PICKUP)
  }

  const openReturnOrderItemModal = (): void => {
    setAssetsManagementModalDirection(FlowDirection.RETURN)
  }

  const closeAssetManagementModal = () => {
    setAssetsManagementModalDirection(FlowDirection.NONE)
  }

  const openOpenOpenDoorsModal = () => {
    setOpenOpenDoorsModal(true)
  }

  const closeOpenDoorsModal = () => {
    setOpenOpenDoorsModal(false)
  }

  const checkIfAuthenticated = (prolongationPageUrl: string): boolean => {
    if (customerInfo.customer) {
      return true
    } else {
      const redirectUrl = `/login?redirectTo=${encodeURIComponent(`${prolongationPageUrl}`)}`

      Router.push(redirectUrl)
      return false
    }
  }

  const redirectToProlongationPage = () => {
    const prolongationPageUrl = getProlongationPageUrl(orderItemInfo)

    const isAuthenticated = checkIfAuthenticated(prolongationPageUrl)
    if (!isAuthenticated) {
      return
    }
    Router.push(prolongationPageUrl)
  }

  if (orderIsLoading || isLoading) {
    return (
      <>
        <Loader margin={0} size="md" />
      </>
    )
  }

  return (
    <>
      <AssetsManagementFlowModal
        direction={assetsManagementModalDirection}
        order={order}
        orderItem={orderItem}
        orderItems={orderItems}
        isOpen={assetsManagementModalDirection !== FlowDirection.NONE}
        onClose={closeAssetManagementModal}
        refetchOrder={refetchOrder}
        devices={devices}
        isPublicPage={isPublicPage}
      />

      <OpenDoorsModal
        orderItem={orderItem}
        isOpen={openOpenDoorsModal}
        onClose={closeOpenDoorsModal}
        devices={devices}
      />

      {pickupStatus !== OrderItemPickupStatus.CANCELED &&
        pickupStatus !== OrderItemPickupStatus.ENDED &&
        pickupStatus !== OrderItemPickupStatus.COMPLETED && (
          <>
            <PinCode orderIsLoading={orderIsLoading} orderItem={orderItem} />

            {hasOutDoors && (
              <LeidButtonWhiteOutlined onClick={openOpenOpenDoorsModal}>
                <LuDoorOpen size={20} />
                {t('openDoor')}
              </LeidButtonWhiteOutlined>
            )}
          </>
        )}

      {pickupStatus === OrderItemPickupStatus.NONE && <></>}
      {pickupStatus === OrderItemPickupStatus.NOT_PAID && (
        <InfoTag color="leid.disabled">
          <Text fontWeight={300} fontSize="smd">
            {t('mustBePaidBeforePickUp')}
          </Text>
        </InfoTag>
      )}
      {pickupStatus === OrderItemPickupStatus.NOT_STARTED && (
        <InfoTag color="leid.disabled">
          <LuKeyRound size={20} />
          <Text fontWeight={300} fontSize="smd">
            {t('readyToBePickedUp')} {orderItem.rentedFrom ? formatItemCardDateDate(orderItem.rentedFrom) : '—'}
          </Text>
        </InfoTag>
      )}
      {pickupStatus === OrderItemPickupStatus.STARTED && (
        <LeidYellowButton onClick={openPickupOrderItemModal}>
          <LuKeyRound size={20} />
          <Text fontWeight={700} fontSize="smd">
            {t('pickupEquipment')}
          </Text>
        </LeidYellowButton>
      )}
      {pickupStatus === OrderItemPickupStatus.CONSUMABLE && (
        <LeidYellowButton onClick={openPickupOrderItemModal}>
          <LuKeyRound size={20} />
          <Text fontWeight={700} fontSize="smd">
            {t('getAmount', { amount: orderItem.amount })}
          </Text>
        </LeidYellowButton>
      )}
      {pickupStatus === OrderItemPickupStatus.PICKED_UP && (
        <LeidYellowButton onClick={openReturnOrderItemModal}>
          <LuKeyRound size={20} />
          <Text fontWeight={700} fontSize="smd">
            {t('returnEquipment')}
          </Text>
        </LeidYellowButton>
      )}
      {pickupStatus === OrderItemPickupStatus.ENDED && (
        <LeidButton onClick={redirectToProlongationPage}>
          <Text fontWeight={700} fontSize="smd">
            {t('extendRentalPeriod')}
          </Text>
        </LeidButton>
      )}
      {pickupStatus === OrderItemPickupStatus.RETURNED && (
        <>
          <InfoTag color="leid.grnn">
            <OrderItemStatusCircle circleStatus={OrderItemCircleStatus.COMPLETED} />
            <Text fontWeight={700} fontSize="smd">
              {t('returned')}
            </Text>
          </InfoTag>

          {orderItem.pickupStatus === PickupStatus.RETURNED && (
            <LeidButton onClick={openReturnOrderItemModal}>
              <LuKeyRound size={20} />
              <Text fontWeight={700} fontSize="smd">
                {t('returnEquipment')}
              </Text>
            </LeidButton>
          )}
        </>
      )}
      {pickupStatus === OrderItemPickupStatus.COMPLETED && (
        <InfoTag color="leid.grnn">
          <OrderItemStatusCircle circleStatus={OrderItemCircleStatus.COMPLETED} />
          <Text fontWeight={700} fontSize="smd">
            {t('completed')}
          </Text>
        </InfoTag>
      )}
      {pickupStatus === OrderItemPickupStatus.CONSUMABLE_PICKEDUP && (
        <InfoTag color="leid.grnn">
          <OrderItemStatusCircle circleStatus={OrderItemCircleStatus.COMPLETED} />
          <Text fontWeight={700} fontSize="smd">
            {t('retrievedAmount', { amount: orderItem.amount })}
          </Text>
        </InfoTag>
      )}
      {pickupStatus === OrderItemPickupStatus.CANCELED && (
        <InfoTag color="leid.grey">
          <OrderItemStatusCircle circleStatus={OrderItemCircleStatus.CANCELED} />
          <Text fontWeight={700} fontSize="smd">
            {t('cancelled')}
          </Text>
        </InfoTag>
      )}
    </>
  )
}

export default OrderItemPickupControl
